import {CHAT_BASE_PATH} from '@root/common/chatBasePath';
import {getGatsbyEnvironment, getPublicMicrositeOrigin} from 'microsites/core/config/getGatsbyConfig';
import {type ReactNode, createContext, useContext} from 'react';
import {buildLocalizedMicrositeHref} from '../routes/pathHelpers';
import {DEFAULT_LANGUAGE_ID} from '../translations/utils/DEFAULT_LANGUAGE_ID';
import {LANGUAGE_DEFINITIONS, type LanguageId} from '../translations/utils/languageManifest';
import {useMicrositeBrand, useMicrositeTranslationContext} from './MicrositeRuntimeProvider';
import type {MicrositeRouteDefinition} from '../routes/types';
import type {BrandBase} from '@root/brands/BrandBase';
import type {DeployEnv} from '@root/common/DeployEnv';

type MicrositeRouteProviderProps = {
  readonly route: MicrositeRouteDefinition;
  readonly children: ReactNode;
};

const MicrositeRouteContext = createContext<MicrositeRouteDefinition | null>(null);

export const MicrositeRouteProvider = ({route, children}: MicrositeRouteProviderProps) => <MicrositeRouteContext.Provider value={route}>{children}</MicrositeRouteContext.Provider>;

// eslint-disable-next-line react-refresh/only-export-components
export const useMicrositeRoute = () => {
  const route = useContext(MicrositeRouteContext);

  if (!route) {
    throw new Error(`Microsite route context missing. Wrap the page with MicrositeRouteProvider.`);
  }

  return route;
};

type CanonicalResolution = {
  readonly requestedEnvironment: DeployEnv;
  readonly canonicalEnvironment: `prod` | `uat`;
  readonly canonicalHost: string;
};

const resolveCanonicalHost = (brand: BrandBase): CanonicalResolution => {
  const requestedEnvironment = getGatsbyEnvironment();
  const canonicalEnvironment = requestedEnvironment === `prod` ? `prod` : `uat`;
  const canonicalHost = getPublicMicrositeOrigin(brand.id) ?? brand.canonical[canonicalEnvironment];

  if (!canonicalHost) {
    throw new Error(`Brand canonical configuration missing host for environment "${canonicalEnvironment}".`);
  }

  return {requestedEnvironment, canonicalEnvironment, canonicalHost};
};

const logCanonicalResolution = (_resolution: CanonicalResolution) => {
  if (typeof console === `undefined`) {
    return;
  }
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMicrositeCanonicalHost = () => {
  const brand = useMicrositeBrand();
  const resolution = resolveCanonicalHost(brand);
  logCanonicalResolution(resolution);
  return resolution;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMicrositeCanonicalUrl = () => {
  const {canonicalHost} = useMicrositeCanonicalHost();
  const {locale} = useMicrositeTranslationContext();
  const route = useMicrositeRoute();

  const languageId: LanguageId = locale;
  const resolvedPath = buildLocalizedMicrositeHref(route, languageId);

  return `${canonicalHost}${resolvedPath}`;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMicrositeChatUrl = () => {
  const {canonicalHost} = useMicrositeCanonicalHost();
  return `${canonicalHost}${CHAT_BASE_PATH}`;
};

export type MicrositeAlternateLink = {
  readonly languageTag: string;
  readonly href: string;
};

const resolveHrefLang = (languageId: LanguageId) => {
  if (!Object.prototype.hasOwnProperty.call(LANGUAGE_DEFINITIONS, languageId)) {
    throw new Error(`Missing language definition for hreflang resolution: "${languageId}".`);
  }

  const definition = LANGUAGE_DEFINITIONS[languageId];

  return definition.lang;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMicrositeAlternateLinks = (): readonly MicrositeAlternateLink[] => {
  const route = useMicrositeRoute();
  const {canonicalHost} = useMicrositeCanonicalHost();

  const localizedEntries = (Object.keys(route.paths) as LanguageId[]).map((languageId) => {
    const finalizedPath = buildLocalizedMicrositeHref(route, languageId);
    const hrefLang = resolveHrefLang(languageId);
    return {
      languageTag: hrefLang,
      href: `${canonicalHost}${finalizedPath}`,
    };
  });

  const defaultLanguageId: LanguageId = DEFAULT_LANGUAGE_ID;
  if (!Object.prototype.hasOwnProperty.call(route.paths, defaultLanguageId)) {
    throw new Error(`Route "${route.id}" is missing default locale "${defaultLanguageId}" required for x-default hreflang.`);
  }

  const defaultHref = `${canonicalHost}${buildLocalizedMicrositeHref(route, defaultLanguageId)}`;

  return [
    ...localizedEntries,
    {
      languageTag: `x-default`,
      href: defaultHref,
    },
  ];
};
